/**
 * Segment Studio - pure helpers.
 *
 * Everything that is "figured out automatically" lives here so the
 * SegmentStudio component stays declarative. No React, no side effects:
 * deterministic functions only, easy to reason about and test.
 *
 * Three responsibilities:
 *   1. NORMALIZATION_PRESETS - simple, named gap-fill recipes. Every
 *      recipe is engineered to leave zero gaps (the fallback chain always
 *      ends with both directions, so any interior OR edge gap resolves to
 *      a real value on the server).
 *   2. deriveAutoConfig() - reads /segment/workspace-analysis and proposes the
 *      whole segment (features, labels, interval, tolerance, window).
 *   3. sanitizePreview() - the last line of defence for the "user must
 *      never see a NaN/null" guarantee. Forward/back-fills anything the
 *      server still left empty and reports a per-column coverage stat so
 *      the UI can be honest about what was inferred.
 */

// ---------------------------------------------------------------------------
// Colour palette - vivid, high-contrast, colour-blind-friendly-ish. Each
// signal gets a stable colour derived from its name so the chart line, the
// grid header pill and the picker chip all agree.
// ---------------------------------------------------------------------------
export const SIGNAL_PALETTE = [
  '#36c5f0', // sky
  '#c0fb6e', // brand lime
  '#ff7ab6', // pink
  '#ffb454', // amber
  '#a78bfa', // violet
  '#2dd4bf', // teal
  '#f87171', // coral
  '#60a5fa', // blue
  '#fbbf24', // gold
  '#4ade80', // green
  '#e879f9', // magenta
  '#38bdf8', // cyan
];

const _hashString = (str) => {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

/**
 * Stable colour for a signal. ``index`` (its position in the selected
 * list) is preferred so adjacent series stay visually distinct; we fall
 * back to a name hash when no index is supplied.
 */
export const colorForSignal = (name, index) => {
  if (typeof index === 'number' && index >= 0) {
    return SIGNAL_PALETTE[index % SIGNAL_PALETTE.length];
  }
  return SIGNAL_PALETTE[_hashString(name) % SIGNAL_PALETTE.length];
};

// ---------------------------------------------------------------------------
// Normalization presets. ``strategy`` matches the backend
// ``normalization_strategy`` contract: { primary_strategy, fallback_strategies }.
// tolerance is injected separately (it tracks the segment interval).
// ---------------------------------------------------------------------------
export const NORMALIZATION_PRESETS = [
  {
    id: 'smart',
    name: 'Smart fill',
    tagline: 'Recommended',
    description:
      'Snap to the closest real reading within tolerance, then carry the last (or next) known value across any gap. No empty cells.',
    accent: '#c0fb6e',
    strategy: {
      primary_strategy: 'nearest_value',
      fallback_strategies: ['previous_value', 'forward_value', 'linear_interpolation'],
    },
  },
  {
    id: 'hold',
    name: 'Hold last',
    tagline: 'Step / staircase',
    description:
      'Repeat the most recent value until a new one arrives. Best for slow-moving signals like prices or balances.',
    accent: '#36c5f0',
    strategy: {
      primary_strategy: 'previous_value',
      fallback_strategies: ['forward_value'],
    },
  },
  {
    id: 'interpolate',
    name: 'Interpolate',
    tagline: 'Smooth lines',
    description:
      'Draw a straight line between known points so the series reads as a continuous trend. Carries the edges when needed.',
    accent: '#a78bfa',
    strategy: {
      primary_strategy: 'linear_interpolation',
      fallback_strategies: ['previous_value', 'forward_value'],
    },
  },
  {
    id: 'zero',
    name: 'Fill with zero',
    tagline: 'Events / counts',
    description:
      'Treat a missing reading as zero. Best for event counts where "no data" genuinely means "nothing happened".',
    accent: '#ffb454',
    strategy: {
      primary_strategy: 'nearest_value',
      fallback_strategies: ['previous_value', 'forward_value', 'zero'],
    },
  },
];

export const getPreset = (id) =>
  NORMALIZATION_PRESETS.find((p) => p.id === id) || NORMALIZATION_PRESETS[0];

// Human-readable labels for the raw strategy ids, used in the advanced panel.
export const STRATEGY_LABELS = {
  nearest_value: 'Nearest value',
  previous_value: 'Previous value',
  forward_value: 'Next value',
  db_previous: 'Previous (from history)',
  db_nearest: 'Nearest (from history)',
  linear_interpolation: 'Linear interpolation',
  zero: 'Zero',
  mean: 'Mean',
  median: 'Median',
  none: 'Leave empty',
};

// ---------------------------------------------------------------------------
// Interval ladder - mirrors core/src/api/segment/management.py
// _round_to_nice_interval so FE-side proposals snap to the same values the
// backend would.
// ---------------------------------------------------------------------------
const NICE_INTERVALS = [
  1, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 21600, 43200,
  86400, 86400 * 2, 86400 * 7, 86400 * 30,
];

export const roundToNiceInterval = (seconds) => {
  if (!seconds || seconds < 1) return 1;
  for (let i = 0; i < NICE_INTERVALS.length; i += 1) {
    if (NICE_INTERVALS[i] >= seconds) return NICE_INTERVALS[i];
  }
  return NICE_INTERVALS[NICE_INTERVALS.length - 1];
};

// ---------------------------------------------------------------------------
// Duration formatting
// ---------------------------------------------------------------------------
export const formatDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 1) return '<1s';
  const units = [
    ['y', 31536000],
    ['mo', 2592000],
    ['w', 604800],
    ['d', 86400],
    ['h', 3600],
    ['m', 60],
    ['s', 1],
  ];
  const parts = [];
  let rest = s;
  for (const [label, size] of units) {
    if (rest >= size) {
      const v = Math.floor(rest / size);
      rest -= v * size;
      parts.push(`${v}${label}`);
    }
    if (parts.length >= 2) break;
  }
  return parts.join(' ') || '0s';
};

export const formatDateUTC = (input, withTime = false) => {
  const date = input instanceof Date ? input : new Date(input);
  if (!date || Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    ...(withTime
      ? { hour: '2-digit', minute: '2-digit', hour12: false }
      : {}),
  }).format(date);
};

/** Compact count formatting (1234 -> "1.2k"). */
export const formatCount = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return String(Math.round(v));
};

/** Compact numeric formatting for grid / chart values. */
export const formatValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'number' || Number.isNaN(value)) return String(value);
  if (Number.isInteger(value)) return String(value);
  const abs = Math.abs(value);
  if (abs !== 0 && (abs >= 1e6 || abs < 1e-3)) return value.toExponential(2);
  // Trim trailing zeros from a 4-dp fixed representation.
  return parseFloat(value.toFixed(4)).toString();
};

// ---------------------------------------------------------------------------
// Field metadata helpers
// ---------------------------------------------------------------------------
export const getFieldRows = (fieldsData) => {
  if (!fieldsData) return [];
  return fieldsData.workspace_anal || fieldsData.fields || [];
};

/** A friendly display name for a (possibly marketplace-aliased) key.
 * Precedence: user-set display name ("o" -> "Open"), then the marketplace
 * original key, then the raw key. */
export const displayKey = (key, fieldRowsByKey) => {
  const row = fieldRowsByKey?.[key];
  if (row?.display_name) return row.display_name;
  if (row?.original_key) return row.original_key;
  return key;
};

// ---------------------------------------------------------------------------
// Engineered features.
//
// Keep this list 1:1 with `core/config/engineered_features.yaml` and
// `core/src/app/segments/ch_segment.py::_engineered_feature_expr`. The backend
// reads ``{ technique, sourceField, name, parameters }`` from each config; the
// extra UI fields (techniqueName/category/sourceFieldType/isRecommended) are
// ignored server-side but kept for rendering.
// ---------------------------------------------------------------------------
export const ENGINEERED_FEATURE_TECHNIQUES = [
  // Lag Features
  { id: 'lag_1', name: 'Lag 1', category: 'Lag Features', description: 'Previous value (1 step back)', dataTypes: ['numeric'] },
  { id: 'lag_2', name: 'Lag 2', category: 'Lag Features', description: 'Previous value (2 steps back)', dataTypes: ['numeric'] },
  { id: 'lag_3', name: 'Lag 3', category: 'Lag Features', description: 'Previous value (3 steps back)', dataTypes: ['numeric'] },
  { id: 'lag_5', name: 'Lag 5', category: 'Lag Features', description: 'Previous value (5 steps back)', dataTypes: ['numeric'] },
  { id: 'lag_7', name: 'Lag 7', category: 'Lag Features', description: 'Previous value (7 steps back)', dataTypes: ['numeric'] },

  // Moving Averages
  { id: 'moving_average_3', name: 'Moving Average (3)', category: 'Moving Averages', description: '3-period moving average', dataTypes: ['numeric'] },
  { id: 'moving_average_7', name: 'Moving Average (7)', category: 'Moving Averages', description: '7-period moving average', dataTypes: ['numeric'] },
  { id: 'moving_average_14', name: 'Moving Average (14)', category: 'Moving Averages', description: '14-period moving average', dataTypes: ['numeric'] },
  { id: 'moving_average_30', name: 'Moving Average (30)', category: 'Moving Averages', description: '30-period moving average', dataTypes: ['numeric'] },

  // Rolling Statistics
  { id: 'rolling_std_7', name: 'Rolling Std (7)', category: 'Rolling Statistics', description: '7-period rolling standard deviation', dataTypes: ['numeric'] },
  { id: 'rolling_min_7', name: 'Rolling Min (7)', category: 'Rolling Statistics', description: '7-period rolling minimum', dataTypes: ['numeric'] },
  { id: 'rolling_max_7', name: 'Rolling Max (7)', category: 'Rolling Statistics', description: '7-period rolling maximum', dataTypes: ['numeric'] },
  { id: 'rolling_median_7', name: 'Rolling Median (7)', category: 'Rolling Statistics', description: '7-period rolling median', dataTypes: ['numeric'] },
  { id: 'rolling_sum_7', name: 'Rolling Sum (7)', category: 'Rolling Statistics', description: '7-period rolling sum', dataTypes: ['numeric'] },

  // Differences and Changes
  { id: 'lag_diff_1', name: 'Lag Difference (1)', category: 'Differences and Changes', description: 'Difference from 1 period ago', dataTypes: ['numeric'] },
  { id: 'lag_pct_change', name: 'Lag % Change', category: 'Differences and Changes', description: 'Percentage change from previous period', dataTypes: ['numeric'] },
  { id: 'momentum_3', name: 'Momentum (3)', category: 'Differences and Changes', description: '3-period momentum', dataTypes: ['numeric'] },
  { id: 'rate_of_change_5', name: 'Rate of Change (5)', category: 'Differences and Changes', description: '5-period rate of change', dataTypes: ['numeric'] },

  // Event Features
  { id: 'release_flag', name: 'Release Flag', category: 'Event Features', description: '1 on the bucket where the source value changed (or first appeared), 0 otherwise.', dataTypes: ['numeric'] },
  { id: 'release_surprise', name: 'Release Surprise', category: 'Event Features', description: 'Raw delta from the previous bucket value.', dataTypes: ['numeric'] },
  { id: 'time_since_release', name: 'Time Since Release', category: 'Event Features', description: 'Seconds since the most recent bucket where the source value changed.', dataTypes: ['numeric'] },

  // Advanced Features
  { id: 'rolling_zscore_7', name: 'Rolling Z-Score (7)', category: 'Advanced Features', description: '7-period rolling z-score', dataTypes: ['numeric'] },
  { id: 'cumulative_return', name: 'Cumulative Return', category: 'Advanced Features', description: 'Cumulative return calculation', dataTypes: ['numeric'] },
  { id: 'rolling_volatility_14', name: 'Rolling Volatility (14)', category: 'Advanced Features', description: '14-period rolling volatility', dataTypes: ['numeric'] },
  { id: 'bollinger_upper_20', name: 'Bollinger Upper (20)', category: 'Advanced Features', description: 'Bollinger band upper bound (20-period)', dataTypes: ['numeric'] },
  { id: 'bollinger_lower_20', name: 'Bollinger Lower (20)', category: 'Advanced Features', description: 'Bollinger band lower bound (20-period)', dataTypes: ['numeric'] },
  { id: 'rolling_bandwidth_20', name: 'Bollinger Bandwidth (20)', category: 'Advanced Features', description: 'Bollinger band bandwidth (20-period)', dataTypes: ['numeric'] },

  // Time Features (timestamp-based)
  { id: 'hour_of_day', name: 'Hour of Day', category: 'Time Features', description: 'Hour of day (0-23)', dataTypes: ['timestamp'] },
  { id: 'day_of_week', name: 'Day of Week', category: 'Time Features', description: 'Day of week (0-6, Monday=0)', dataTypes: ['timestamp'] },
  { id: 'day_of_month', name: 'Day of Month', category: 'Time Features', description: 'Day of month (1-31)', dataTypes: ['timestamp'] },
  { id: 'day_of_year', name: 'Day of Year', category: 'Time Features', description: 'Day of year (1-366)', dataTypes: ['timestamp'] },
  { id: 'week_of_year', name: 'Week of Year', category: 'Time Features', description: 'ISO week of year (1-53)', dataTypes: ['timestamp'] },
  { id: 'month_of_year', name: 'Month of Year', category: 'Time Features', description: 'Month of year (1-12)', dataTypes: ['timestamp'] },
  { id: 'quarter_of_year', name: 'Quarter of Year', category: 'Time Features', description: 'Quarter of year (1-4)', dataTypes: ['timestamp'] },
  { id: 'year', name: 'Year', category: 'Time Features', description: 'Calendar year', dataTypes: ['timestamp'] },
  { id: 'is_weekend', name: 'Is Weekend', category: 'Time Features', description: 'Weekend flag (Saturday/Sunday)', dataTypes: ['timestamp'] },
  { id: 'is_business_day', name: 'Is Business Day', category: 'Time Features', description: 'Weekday flag (Mon-Fri)', dataTypes: ['timestamp'] },
  { id: 'is_month_start', name: 'Is Month Start', category: 'Time Features', description: 'Flag for first day of month', dataTypes: ['timestamp'] },
  { id: 'is_month_end', name: 'Is Month End', category: 'Time Features', description: 'Flag for last day of month', dataTypes: ['timestamp'] },
  { id: 'is_quarter_start', name: 'Is Quarter Start', category: 'Time Features', description: 'Flag for first day of quarter', dataTypes: ['timestamp'] },
  { id: 'is_quarter_end', name: 'Is Quarter End', category: 'Time Features', description: 'Flag for last day of quarter', dataTypes: ['timestamp'] },
  { id: 'is_year_start', name: 'Is Year Start', category: 'Time Features', description: 'Flag for first day of year', dataTypes: ['timestamp'] },
  { id: 'is_year_end', name: 'Is Year End', category: 'Time Features', description: 'Flag for last day of year', dataTypes: ['timestamp'] },
];

const NUMERIC_FIELD_TYPES = new Set([
  'numeric', 'number', 'float', 'double', 'integer',
  'continuous_numerical', 'discrete_numerical', 'binary_boolean',
]);
const TIMESTAMP_FIELD_TYPES = new Set([
  'timestamp', 'datetime', 'date', 'time', 'datetime_timestamp',
]);

export const getTechnique = (id) =>
  ENGINEERED_FEATURE_TECHNIQUES.find((t) => t.id === id) || null;

export const isTimeTechnique = (id) => {
  const t = getTechnique(id);
  return !!t && t.category === 'Time Features';
};

/** Stable identity for a (technique, source) pair - used to dedupe. */
export const engineeredKey = (ef) => `${ef.technique}__${ef.sourceField}`;

/** Fields whose type is compatible with a technique's accepted data types. */
export const compatibleFieldsForTechnique = (techniqueId, fieldRows) => {
  const tech = getTechnique(techniqueId);
  if (!tech) return [];
  const wantsNumeric = tech.dataTypes.includes('numeric');
  const wantsTimestamp = tech.dataTypes.includes('timestamp');
  return (fieldRows || []).filter((f) => {
    const t = (f.type || '').toLowerCase();
    return (wantsNumeric && NUMERIC_FIELD_TYPES.has(t)) ||
      (wantsTimestamp && TIMESTAMP_FIELD_TYPES.has(t));
  });
};

/** Build a complete engineered-feature config from a technique + source. */
export const makeEngineeredFeature = (techniqueId, sourceFieldKey, fieldRows) => {
  const tech = getTechnique(techniqueId);
  if (!tech) return null;
  if (tech.category === 'Time Features') {
    const tsField = (fieldRows || []).find((f) =>
      TIMESTAMP_FIELD_TYPES.has((f.type || '').toLowerCase()),
    );
    return {
      technique: techniqueId,
      techniqueName: tech.name,
      sourceField: tsField ? tsField.key : 'ts',
      sourceFieldType: tsField ? tsField.type : 'timestamp',
      name: techniqueId, // time features use the technique id as the column name
      category: tech.category,
      parameters: tech.parameters || {},
    };
  }
  const sf = (fieldRows || []).find((f) => f.key === sourceFieldKey);
  if (!sf) return null;
  return {
    technique: techniqueId,
    techniqueName: tech.name,
    sourceField: sourceFieldKey,
    sourceFieldType: sf.type,
    name: `${sourceFieldKey}_${techniqueId}`,
    category: tech.category,
  };
};

/** Engineered-feature configs recommended by the workspace analysis. */
export const recommendedEngineeredConfigs = (fieldsData, fieldRows) => {
  const rec = fieldsData?.latest_analysis?.results?.feature_recommendations?.engineered;
  if (!rec || typeof rec !== 'object') return [];
  const byKey = {};
  (fieldRows || []).forEach((f) => {
    byKey[f.key] = f;
  });
  const out = [];
  Object.entries(rec).forEach(([techniqueId, data]) => {
    const tech = getTechnique(techniqueId);
    if (!tech || !data || !Array.isArray(data.features)) return;
    data.features.forEach((src) => {
      const sf = byKey[src];
      if (!sf) return;
      out.push({
        technique: techniqueId,
        techniqueName: tech.name,
        sourceField: src,
        sourceFieldType: sf.type,
        name: `${src}_${techniqueId}`,
        category: tech.category,
        isRecommended: true,
      });
    });
  });
  return out;
};

// ---------------------------------------------------------------------------
// Auto configuration - the "we figure out the complex stuff" core.
// ---------------------------------------------------------------------------
const _recNames = (arr) =>
  (arr || [])
    .map((x) => (typeof x === 'string' ? x : x && x.name))
    .filter(Boolean);

/**
 * Propose a complete starting point from /segment/workspace-analysis.
 *
 * Returns a plain object the component seeds its state from exactly once.
 * Heuristics, in order of preference:
 *   - labels   <- analysis label_recommendations, else label-like candidates
 *   - features <- analysis feature_recommendations.available (minus labels)
 *   - interval <- recommended_settings.default_interval (slowest-key cadence)
 *   - tolerance<- recommended_settings.default_tolerance (~1.5x interval)
 *   - window   <- the full workspace span (anchored to the latest signal)
 */
export const deriveAutoConfig = (fieldsData) => {
  const out = {
    labels: [],
    features: [],
    interval: 3600,
    tolerance: 5400,
    tailSeconds: 31536000,
    workspaceStartMs: null,
    workspaceEndMs: null,
    explanation: '',
  };
  if (!fieldsData) return out;

  const results = fieldsData.latest_analysis?.results || {};
  const allKeys = getFieldRows(fieldsData).map((f) => f.key);
  const allKeySet = new Set(allKeys);

  // Labels.
  let labels = _recNames(results.label_recommendations).filter((k) =>
    allKeySet.has(k),
  );
  if (labels.length === 0) {
    const candidates =
      results.metadata?.data_priors?.leakage?.label_candidates || [];
    labels = candidates.filter((k) => allKeySet.has(k)).slice(0, 1);
  }
  labels = labels.slice(0, 3);

  // Features: recommended, minus anything we just picked as a label.
  const labelSet = new Set(labels);
  let features = _recNames(results.feature_recommendations?.available)
    .filter((k) => allKeySet.has(k) && !labelSet.has(k))
    .slice(0, 8);

  // Fallback: if analysis gave us nothing, seed from the numeric fields so
  // the studio is never empty on first load.
  if (features.length === 0) {
    features = getFieldRows(fieldsData)
      .filter((f) => f.type === 'number' && !labelSet.has(f.key))
      .map((f) => f.key)
      .slice(0, 6);
  }
  if (labels.length === 0 && features.length > 1) {
    // Promote one feature to a label so create() has a valid pair.
    labels = [features[0]];
    features = features.slice(1);
  }

  out.labels = labels;
  out.features = features;

  // Workspace window (needed before interval so the bucket clamp can apply).
  const ptr = fieldsData.time_range?.workspace_time_range;
  if (ptr?.first_signal && ptr?.last_signal) {
    const startMs = new Date(ptr.first_signal).getTime();
    const endMs = new Date(ptr.last_signal).getTime();
    if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs > startMs) {
      out.workspaceStartMs = startMs;
      out.workspaceEndMs = endMs;
      out.tailSeconds = Math.max(1, Math.round((endMs - startMs) / 1000));
    }
  }

  // Interval / tolerance. Prefer the REAL cadence of the chosen signals so
  // we never default to a 1-second granularity that produces millions of
  // empty buckets. Fall back to recommended_settings (also bucket-clamped).
  const rs = fieldsData.recommended_settings || {};
  const selected = [...out.labels, ...out.features];
  const cadence = cadenceIntervalForKeys(fieldsData, selected, out.tailSeconds);
  if (cadence) {
    out.interval = cadence;
  } else if (rs.default_interval) {
    out.interval = Math.max(1, Math.round(rs.default_interval));
    const floor = out.tailSeconds / MAX_SEGMENT_BUCKETS;
    if (out.interval < floor) out.interval = roundToNiceInterval(floor);
  }
  out.tolerance = Math.max(1, Math.round(out.interval * 1.5));
  if (rs.default_tolerance && !cadence) {
    out.tolerance = Math.max(1, Math.round(rs.default_tolerance));
  }
  out.explanation = rs.explanation || '';

  // Default to Live when the data looks like an actively-flowing feed - i.e.
  // the latest signal is "near" now (within a handful of intervals, or a day,
  // whichever is larger). Historical archives (last signal months/years ago)
  // default to a fixed window.
  out.live = false;
  if (out.workspaceEndMs != null) {
    const gap = Date.now() - out.workspaceEndMs; // >0 = latest signal is in the past
    const nearThreshold = Math.max(86400, out.interval * 6) * 1000;
    // Live only when the latest signal sits close to *now* on BOTH sides.
    // Far in the past = a historical archive. Far in the FUTURE = a corrupt
    // timestamp (you can't record tomorrow's data) - which previously made
    // the studio default to Live. Neither should auto-enable Live.
    out.live = gap >= -nearThreshold && gap <= nearThreshold;
  }

  return out;
};

/**
 * The smallest sensible window (in seconds) given the data. We never let the
 * range slider drop below a few buckets at the chosen interval, otherwise the
 * preview would have nothing to draw.
 */
export const minWindowSeconds = (interval) =>
  Math.max(60, Math.round((Number(interval) || 60) * 12));

// Hard ceiling on how many buckets a segment can resolve to. A 1-second
// interval over a month is 2.6M rows of mostly-empty cells - useless for a
// preview AND a terrible default for the real segment. We never let the
// interval imply more than this many rows for the chosen window.
export const MAX_SEGMENT_BUCKETS = 200000;

/**
 * Derive the interval (seconds) from the REAL cadence of the selected
 * signals, not the workspace's declared granularity.
 *
 * We take the slowest selected signal's average inter-arrival (so even the
 * laziest channel lands a reading in most buckets) from
 * ``time_range.field_time_ranges[key].avg_interval_seconds``, snap it to the
 * nice ladder, then clamp it up if the resulting window would blow past
 * MAX_SEGMENT_BUCKETS. Returns null when we have no cadence signal at all
 * (caller should fall back to recommended_settings).
 */
export const cadenceIntervalForKeys = (fieldsData, keys, tailSeconds) => {
  const ftr = fieldsData?.time_range?.field_time_ranges || {};
  let slowest = 0;
  (keys || []).forEach((k) => {
    const r = ftr[k];
    const avg = r && r.avg_interval_seconds;
    if (avg && avg > slowest) slowest = avg;
  });

  let interval = slowest > 0 ? slowest : null;

  // Bucket-count safety clamp (applies even when we have a cadence).
  if (tailSeconds && tailSeconds > 0) {
    const floorByCount = tailSeconds / MAX_SEGMENT_BUCKETS;
    if (interval == null || interval < floorByCount) {
      interval = Math.max(interval || 0, floorByCount);
    }
  }
  if (interval == null) return null;
  return roundToNiceInterval(interval);
};

/**
 * Per-signal density tracks for the range timeline.
 *
 * Uses the REAL signal distribution the backend already computed
 * (``data_distribution.bins[].fields[key]`` - counts per time bin across the
 * whole workspace span) so each signal renders as a little histogram that
 * honestly shows where its data is dense, where it's sparse, and where the
 * gaps are. Falls back to a simple coverage span (first->last signal) when no
 * distribution is available.
 *
 * Every track is aligned to the SAME workspace span ``[workspaceStartMs,
 * workspaceEndMs]`` so the selected-window overlay lines up across all rows.
 */
export const buildSignalTracks = (fieldsData, keys, colorMap, fieldRowsByKey) => {
  const dist = fieldsData?.data_distribution;
  const bins = Array.isArray(dist?.bins) ? dist.bins : [];
  const ftr = fieldsData?.time_range?.field_time_ranges || {};
  const ptr = fieldsData?.time_range?.workspace_time_range;
  let spanStart = null;
  let spanEnd = null;
  if (ptr?.first_signal && ptr?.last_signal) {
    spanStart = new Date(ptr.first_signal).getTime();
    spanEnd = new Date(ptr.last_signal).getTime();
  }
  const span = spanStart != null && spanEnd != null ? spanEnd - spanStart : 0;

  return (keys || []).map((key) => {
    const color = (colorMap && colorMap[key]) || colorForSignal(key);
    const label = displayKey(key, fieldRowsByKey);

    // Density histogram (preferred).
    let histogram = [];
    if (bins.length) {
      const counts = bins.map((b) => (b.fields && b.fields[key]) || 0);
      const max = counts.reduce((m, c) => (c > m ? c : m), 0);
      histogram = counts.map((c) => ({
        count: c,
        intensity: max > 0 ? Math.sqrt(c / max) : 0,
      }));
    }

    // Coverage span fallback / supplement.
    const r = ftr[key];
    let startFrac = 0;
    let endFrac = 1;
    if (r && span > 0) {
      startFrac = Math.max(0, Math.min(1, (new Date(r.first_signal).getTime() - spanStart) / span));
      endFrac = Math.max(0, Math.min(1, (new Date(r.last_signal).getTime() - spanStart) / span));
    }

    const totalCount =
      (dist?.field_distribution && dist.field_distribution[key]) ||
      (r && r.signal_count) ||
      0;
    const cadenceSeconds = r?.avg_interval_seconds || null;

    return {
      key,
      color,
      label,
      histogram,
      startFrac,
      endFrac,
      totalCount,
      cadenceSeconds,
      cadenceLabel: cadenceSeconds ? `every ${formatDuration(cadenceSeconds)}` : 'cadence n/a',
    };
  });
};

// ---------------------------------------------------------------------------
// Config assembly - the single source of truth for both PREVIEW and CREATE.
// ---------------------------------------------------------------------------
/**
 * Build the segment payload from studio state.
 *
 * @param {object} state - {
 *   name, description, features[], labels[], interval, tolerance,
 *   tailSeconds, live, windowStartMs, windowEndMs,
 *   preset (id), advanced {globalStrategy, perField}, workspaceId
 * }
 *
 * Windowing contract (matches the backend ``resolve_segment_bounds``):
 *   - live=true  -> rolling window [now - tail_seconds, now]; the explicit
 *     window is omitted.
 *   - live=false -> explicit historical window [window_start, window_end]
 *     (ISO-8601). ``tail_seconds`` is still sent as a legacy fallback.
 */
export const buildSegmentConfig = (state) => {
  const preset = getPreset(state.preset);
  const tolerance = Math.max(1, Math.round(Number(state.tolerance) || 1));

  // Global strategy: advanced overrides win, otherwise the preset recipe.
  const adv = state.advanced || {};
  const globalStrategy = adv.globalStrategy || preset.strategy;

  const normalization_strategy = {
    primary_strategy: globalStrategy.primary_strategy,
    fallback_strategies: [...(globalStrategy.fallback_strategies || [])],
    tolerance,
  };

  // Per-field overrides (advanced only). Strip empties so the payload stays
  // clean and the backend default applies.
  const segment_strategies = {};
  Object.entries(adv.perField || {}).forEach(([key, strat]) => {
    if (strat && strat.primary_strategy) {
      segment_strategies[key] = {
        primary_strategy: strat.primary_strategy,
        fallback_strategies: [...(strat.fallback_strategies || [])],
        tolerance,
      };
    }
  });

  const live = Boolean(state.live);
  const toIso = (ms) =>
    ms != null && !Number.isNaN(ms) ? new Date(ms).toISOString() : null;

  return {
    name: String(state.name || ''),
    description: String(state.description || ''),
    purpose: String(state.purpose || ''),
    workspace_id: state.workspaceId,
    features: Array.from(new Set(state.features || [])),
    labels: Array.from(new Set(state.labels || [])),
    interval: Math.max(1, Math.round(Number(state.interval) || 1)),
    tolerance,
    tail_seconds: Math.max(1, Math.round(Number(state.tailSeconds) || 1)),
    // Explicit historical window (omitted/null for live segments).
    window_start: live ? null : toIso(state.windowStartMs),
    window_end: live ? null : toIso(state.windowEndMs),
    strategy: 'approximated',
    live,
    time_zone: 'UTC',
    normalization_strategy,
    segment_strategies,
    engineered_features: (state.engineeredFeatures || []).map((ef) => ({
      technique: ef.technique,
      techniqueName: ef.techniqueName,
      sourceField: ef.sourceField,
      sourceFieldType: ef.sourceFieldType,
      name: ef.name,
      category: ef.category,
      ...(ef.parameters ? { parameters: ef.parameters } : {}),
    })),
  };
};

// ---------------------------------------------------------------------------
// Preview sanitisation - THE no-NaN guarantee.
// ---------------------------------------------------------------------------
const _isNum = (v) => typeof v === 'number' && !Number.isNaN(v);

/**
 * Forward-fill then back-fill a column in place-ish (returns a new array),
 * tracking which indices were inferred. Numeric columns that are entirely
 * empty collapse to 0; string columns to ''.
 */
const _fillColumn = (values, isNumeric) => {
  const out = new Array(values.length);
  const mask = new Array(values.length).fill(false);
  const isEmpty = (v) =>
    v === null || v === undefined || (isNumeric && Number.isNaN(v)) || v === 'NaN';

  // Forward fill.
  let last = null;
  let haveLast = false;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (!isEmpty(v)) {
      out[i] = v;
      last = v;
      haveLast = true;
    } else if (haveLast) {
      out[i] = last;
      mask[i] = true;
    } else {
      out[i] = null; // resolved in the back-fill pass
      mask[i] = true;
    }
  }
  // Back fill leading empties.
  let next = null;
  let haveNext = false;
  for (let i = values.length - 1; i >= 0; i -= 1) {
    if (out[i] === null) {
      if (haveNext) {
        out[i] = next;
      } else {
        out[i] = isNumeric ? 0 : '';
      }
      mask[i] = true;
    } else {
      next = out[i];
      haveNext = true;
    }
  }
  return { values: out, mask };
};

/**
 * Turn the raw /segment/preview payload into a render-ready, gap-free shape.
 *
 * @param previewData - the redux slice value (``{ data: {...} }`` or the
 *   inner object directly).
 * @param fieldRowsByKey - map of key -> field row (for type + marketplace).
 * @returns {
 *   timestamps: string[],
 *   columns: [{ key, label, marketplace, isNumeric, isLabel, values[], mask[],
 *               realCount, total, coverage }],
 *   totalRows, pageSize, currentPage, totalPages, segmentInfo, timeRange,
 *   filledCells, totalCells, hadGaps
 * }
 */
export const sanitizePreview = (previewData, fieldRowsByKey = {}) => {
  if (!previewData) return null;
  const root = previewData.data ? previewData.data : previewData;
  const dataObject = root.data;
  if (!dataObject) return null;

  const timestamps = dataObject.timestamp || [];
  const total = timestamps.length;
  const fieldInfo = root.field_info || {};
  const labelSet = new Set(fieldInfo.labels || []);
  const fieldMeta = fieldInfo.field_meta || {};

  const colKeys = Object.keys(dataObject).filter((c) => c !== 'timestamp');

  let filledCells = 0;
  let totalCells = 0;

  const columns = colKeys.map((key) => {
    const raw = dataObject[key] || [];
    // Numeric if any present value is a finite number.
    const isNumeric = raw.some((v) => _isNum(v));
    const realCount = raw.reduce(
      (acc, v) =>
        acc + (v !== null && v !== undefined && v !== 'NaN' ? 1 : 0),
      0,
    );
    const { values, mask } = _fillColumn(raw, isNumeric);
    const filled = mask.reduce((a, m) => a + (m ? 1 : 0), 0);
    filledCells += filled;
    totalCells += total;

    const meta = fieldMeta[key];
    return {
      key,
      label: meta?.display_name || meta?.original_key || key,
      marketplace: meta?.marketplace || null,
      isNumeric,
      isLabel: labelSet.has(key),
      values,
      mask,
      realCount,
      total,
      coverage: total > 0 ? realCount / total : 1,
    };
  });

  const pag = root.pagination || {};
  return {
    timestamps,
    columns,
    totalRows: Math.max(0, Number(pag.total_rows) || total),
    pageSize: Math.max(1, Number(pag.page_size) || total || 1),
    currentPage: Math.max(1, Number(pag.current_page) || 1),
    totalPages: Math.max(1, Number(pag.total_pages) || 1),
    segmentInfo: root.segment_info || {},
    timeRange: root.time_range || {},
    filledCells,
    totalCells,
    hadGaps: filledCells > 0,
  };
};
